import { type Promotion } from '@/features/shop/services/Campaigns/types'
import { type AdBannerData, type CMSPromotionData } from '@/features/cms/components/CMS/types'
import { getRetailerFeaturedPromotionsUrl } from '@shared/constants/routes'
import { stringify } from '@/features/shared/utils/queryString'
import { type SearchParams } from '@/features/shop/services/Search/types'

type PromotionDataInput = {
  data: CMSPromotionData['data']
  metadata?: {
    read_operation_info?: {
      source_object?: {
        object?: Promotion
        type?: 'promotion'
      }
    }
  }
}

export const getPromotionFromData = (promotionData: PromotionDataInput): Promotion => {
  const sourceObject = promotionData.metadata?.read_operation_info?.source_object?.object

  const {
    heading: display_name,
    sub_heading: display_description,
    terms,
    full_terms_title,
    full_terms_url,
    available_stores,
    cta_text,
    original_price,
    expiration_date,
    image,
    background_color,
    text_color,
    store_cards,
    skip_store_selection,
  } = promotionData.data

  return {
    ...sourceObject,
    display_name,
    display_description,
    terms,
    full_terms_title,
    full_terms_url,
    button_cta: cta_text,
    valid_through: expiration_date,
    background_color,
    text_color,
    promotion_params: {
      ...sourceObject?.promotion_params,
      ...(image && {
        images: [{ image_url: image.src }],
      }),
      ...(original_price && {
        original_price,
      }),
    },
    available_store_ids:
      store_cards?.map((store) => store.data.retailer_id) ??
      available_stores?.map((store) => store.data.retailer_id) ??
      [],
    skip_store_selection,
  }
}

export const getPromotionFromAdBannerData = (adBannerData: AdBannerData): Promotion => {
  const {
    data,
    metadata: { read_operation_info },
  } = adBannerData

  return getPromotionFromData({
    data: {
      heading: data.heading ?? '',
      available_stores: data.available_stores,
    },
    metadata: {
      read_operation_info: {
        source_object: read_operation_info?.source_object,
      },
    },
  })
}

export const getSearchRouteWithPromotion = (
  promotion: Promotion,
  storeName: string,
): string | null => {
  const { category_id, search_url, is_marketing_category, relative_link } =
    promotion.promotion_params ?? {}

  if (relative_link) return relative_link
  if (search_url) return search_url

  if (category_id) {
    const params: SearchParams = {
      query: '',
      [is_marketing_category ? 'promotion_category_id' : 'category_id']: category_id,
    }
    const stringifiedParams = stringify(params)
    return `${getRetailerFeaturedPromotionsUrl(storeName, category_id)}?${stringifiedParams}`
  }

  return null
}
